import { type ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

import { AdminIcon } from "@/components/admin/icon";

/**
 * Coquille de modal (habillage uniquement : overlay, boîte centrée, titre,
 * fermeture par Échap / clic extérieur). Le contenu — formulaire, champs,
 * boutons — est fourni par l'appelant : chaque écran a son propre formulaire
 * dédié, il n'y a PAS de formulaire générique ici.
 */
export function Modal({
	title,
	subtitle,
	onClose,
	children,
	width = 460,
}: {
	title: string;
	subtitle?: ReactNode;
	onClose: () => void;
	children: ReactNode;
	width?: number;
}) {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="modal-title"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
			onKeyDown={(e) => {
				if (e.target === e.currentTarget && e.key === "Escape") onClose();
			}}
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1000,
				background: "rgba(20,16,12,0.45)",
				display: "grid",
				placeItems: "center",
				padding: 16,
				border: "none",
				width: "100vw",
				height: "100vh",
				textAlign: "left",
				cursor: "default",
			}}
		>
			<div
				style={{
					width: `min(${width}px, 100%)`,
					background: "var(--a-surface)",
					border: "1px solid var(--a-border)",
					borderRadius: "var(--a-r-lg, 12px)",
					boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35)",
					overflow: "visible",
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						gap: 12,
						padding: "16px 18px",
						borderBottom: "1px solid var(--a-border)",
					}}
				>
					<div style={{ flex: 1 }}>
						<h3
							id="modal-title"
							style={{
								margin: 0,
								fontSize: 16,
								fontWeight: 700,
								color: "var(--a-ink)",
							}}
						>
							{title}
						</h3>
						{subtitle && (
							<div
								style={{
									marginTop: 4,
									fontSize: 12.5,
									color: "var(--a-ink-soft)",
									lineHeight: 1.45,
								}}
							>
								{subtitle}
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Fermer"
						style={{
							opacity: 0.6,
							cursor: "pointer",
							marginTop: 2,
							flexShrink: 0,
						}}
					>
						<AdminIcon n="close" s={16} />
					</button>
				</div>
				<div style={{ padding: 18 }}>{children}</div>
			</div>
		</div>,
		document.body,
	);
}

/** Pied de modal standard (Annuler + bouton principal). Optionnel — purement visuel. */
export function ModalFooter({ children }: { children: ReactNode }) {
	return (
		<div
			style={{
				display: "flex",
				justifyContent: "flex-end",
				gap: 8,
				marginTop: 16,
			}}
		>
			{children}
		</div>
	);
}
